import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { getAuditReport } from "@/api/audit";
import { FlagCard } from "@/components/FlagCard";
import type { ApiError, AuditReport, Severity } from "@/types";

interface Props {
  appointmentId: string;
  /** Rendered above the metrics once the report has loaded (e.g. appointment details). */
  header?: (report: AuditReport) => ReactNode;
}

/** Metrics, severity tabs and flag cards for one appointment's audit. */
export function AuditReportView({ appointmentId, header }: Props) {
  const [severityFilter, setSeverityFilter] = useState<Severity | "all">("all");

  const report = useQuery<AuditReport, ApiError>({
    queryKey: ["audit", "report", appointmentId, severityFilter],
    queryFn: () => getAuditReport(appointmentId, severityFilter === "all" ? undefined : severityFilter),
  });

  if (report.isLoading) {
    return (
      <div className="card loading">
        <div className="spinner" />
        <div>Loading report…</div>
      </div>
    );
  }

  if (report.error) {
    return <div className="card form-error">Failed to load report: {report.error.message}</div>;
  }

  if (!report.data) return null;
  const data = report.data;

  return (
    <>
      {header?.(data)}

      <div className="metrics">
        <div className="metric">
          <div className="label">Total flags</div>
          <div className="value">{data.total_flags}</div>
        </div>
        <div className="metric critical">
          <div className="label">Critical</div>
          <div className="value">{data.critical_flags}</div>
        </div>
        <div className="metric warning">
          <div className="label">Warnings</div>
          <div className="value">{data.warning_flags}</div>
        </div>
        <div className="metric">
          <div className="label">Leakage</div>
          <div className="value">${data.total_leakage_usd.toFixed(2)}</div>
        </div>
      </div>

      <div className="tabs">
        {(["all", "red", "yellow", "green"] as const).map((s) => (
          <button
            key={s}
            className={`tab ${severityFilter === s ? "active" : ""}`}
            onClick={() => setSeverityFilter(s)}
          >
            {s === "all" ? "All" : s}
          </button>
        ))}
      </div>

      {data.flags.length === 0 ? (
        <div className="card muted">No flags in this category.</div>
      ) : (
        <div className="flag-list">
          {data.flags.map((f) => (
            <FlagCard key={f.id} flag={f} appointmentId={appointmentId} synced={data.appointment?.external_source === "ezyvet"} />
          ))}
        </div>
      )}
    </>
  );
}
